import type { AuthzContextState } from './useAuthzContextQuery'
import type { ConfigArea, PermissionSetEntity } from '@omnea-app/authz'

type ResolvedAuthzContext = Extract<AuthzContextState, { status: 'resolved' }>

/**
 * What an `fga` rule may read. Only the settled context appears: `loading` and `unavailable` are
 * decided by the resolver, never by a rule author. The caller's role is absent, so an `fga` rule
 * cannot widen itself past the rollout its own arm governs.
 */
type ResolvedAuthorizationSources = {
  authz: ResolvedAuthzContext
}

export type FgaAuthorizationRule = (sources: ResolvedAuthorizationSources) => boolean

export const hasEntityAuthorization =
  (entity: PermissionSetEntity): FgaAuthorizationRule =>
  ({ authz }) =>
    authz.entities.has(entity)

/** Whether the caller may add a record of this family, which entitlement to the family alone does not confer. */
export const creates =
  (entity: PermissionSetEntity): FgaAuthorizationRule =>
  ({ authz }) =>
    authz.entities.get(entity)?.canCreate === true

/** Whether the caller administers a configuration area — what a settings route's config gate asks. */
export const managesConfigArea =
  (area: ConfigArea): FgaAuthorizationRule =>
  ({ authz }) =>
    authz.managedAreas.has(area)

export const managesPermissions: FgaAuthorizationRule = ({ authz }) => authz.canManagePermissions

/** Whether the caller may make another user a super admin — a system admin, so nobody promotes past themselves. */
export const promotesToSuperAdmin: FgaAuthorizationRule = ({ authz }) => authz.canPromoteToSuperAdmin
